/*
 * EVRYDAY Desi Pop runtime (vanilla, no dependencies, every template).
 *  - reveals, doodle draw-on, word reveals, sticker wobbles, count-ups (IntersectionObserver)
 *  - pointer tilt for [data-dp-tilt] (mouse + >=1024px only)
 *  - fly-to-cart + cart bounce + first-add confetti on any [data-evd-atc]
 *  - product-image morph hook for Horizon's view transitions, and a JS curtain fallback
 *    for browsers without cross-document view transitions
 *  - DP.addItems() for multi-item adds (stack builder), DP.confetti(), DP.store
 * Everything is progressive: server HTML is complete without it, and reduced motion
 * turns all of it into instant state changes.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  html.classList.add('dp-js');

  var DP = (window.DP = window.DP || {});
  DP.reduced = function () { return reduce.matches; };

  /* ---------------- storage (private mode safe) ---------------- */
  DP.store = {
    get: function (k, fallback) {
      try { var v = localStorage.getItem('dp:' + k); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
    },
    set: function (k, v) { try { localStorage.setItem('dp:' + k, JSON.stringify(v)); } catch (e) {} },
    del: function (k) { try { localStorage.removeItem('dp:' + k); } catch (e) {} }
  };

  /* ---------------- in-view machinery ---------------- */
  var io = null;
  function onIn(el) {
    if (el.matches('[data-dp-reveal], .dp-words, [data-dp-in]')) el.classList.add('is-in');
    if (el.matches('[data-dp-draw]')) el.classList.add('is-drawn');
    if (el.matches('.dp-sticker[data-dp-wobble]')) el.classList.add('is-wobble');
    if (el.matches('[data-dp-count]')) countUp(el);
    el.dispatchEvent(new CustomEvent('dp:in', { bubbles: true }));
  }
  function observe(root) {
    var els = (root || document).querySelectorAll('[data-dp-reveal], .dp-words, [data-dp-draw], .dp-sticker[data-dp-wobble], [data-dp-count], [data-dp-in]');
    if (reduce.matches || designMode || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, onIn);
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { io.unobserve(e.target); onIn(e.target); }
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0 });
    }
    Array.prototype.forEach.call(els, function (el, i) {
      if (el.matches('[data-dp-draw]')) {
        Array.prototype.forEach.call(el.querySelectorAll('path'), function (p, j) { p.style.setProperty('--i', j); });
      }
      io.observe(el);
    });
  }
  DP.observe = observe;

  /* ---------------- money (follows the shop's money format, so JS and Liquid prices match) ---------------- */
  DP.money = function (cents) {
    var fmt = window.DP_MONEY_FORMAT || '\u20B9{{amount_no_decimals}}';
    var n = Math.round(Number(cents) || 0);
    function group(num, dec, th, ds) {
      var parts = (num / 100).toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.length > 1 ? parts[0] + ds + parts[1] : parts[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return group(n, 0, ',', '.');
        case 'amount_with_comma_separator': return group(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(n, 2, "'", '.');
        default: return group(n, 2, ',', '.').replace(/\.00$/, '');
      }
    }).replace(/<[^>]*>/g, '');
  };

  /* ---------------- count-up (final value is in the HTML) ---------------- */
  function countUp(el) {
    var target = parseFloat(el.getAttribute('data-dp-count'));
    if (isNaN(target) || reduce.matches) return;
    var dec = (String(el.getAttribute('data-dp-count')).split('.')[1] || '').length;
    var suffix = el.getAttribute('data-dp-suffix') || '';
    var dur = 1100, t0 = null;
    function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / dur);
      var e = 1 - Math.pow(1 - k, 3);
      el.textContent = (target * e).toFixed(dec) + suffix;
      if (k < 1) requestAnimationFrame(step); else el.textContent = target.toFixed(dec) + suffix;
    }
    requestAnimationFrame(step);
  }
  DP.tween = function (el, to, opts) {
    opts = opts || {};
    var dec = opts.decimals || 0;
    var fmt = opts.format || function (v) { return Number(v).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec }); };
    if (reduce.matches) { el.textContent = fmt(to); return; }
    var from = parseFloat(String(el.getAttribute('data-v') || el.textContent).replace(/[^0-9.\-]/g, '')) || 0;
    el.setAttribute('data-v', to);
    var t0 = null, dur = opts.duration || 500;
    if (el.__raf) cancelAnimationFrame(el.__raf);
    function step(t) {
      if (!t0) t0 = t;
      var k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      el.textContent = fmt(from + (to - from) * e);
      if (k < 1) el.__raf = requestAnimationFrame(step);
    }
    el.__raf = requestAnimationFrame(step);
  };

  /* ---------------- pointer tilt ---------------- */
  var fine = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  function bindTilt(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-dp-tilt]'), function (el) {
      if (el.__tilt) return;
      el.__tilt = true;
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || reduce.matches || !fine.matches) return;
        var r = el.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5, py = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform = 'perspective(900px) rotateY(' + (px * 14).toFixed(2) + 'deg) rotateX(' + (-py * 10).toFixed(2) + 'deg) translateY(-6px)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* ---------------- confetti ---------------- */
  var CONFETTI = ['#f26722', '#ffe27a', '#2b3fae', '#5a3a26', '#b8955a', '#f5d000', '#ff8a3d', '#c9e7b3'];
  DP.confetti = function (opts) {
    if (reduce.matches) return;
    opts = opts || {};
    var n = opts.count || 80;
    var ox = opts.x != null ? opts.x : window.innerWidth / 2, oy = opts.y != null ? opts.y : window.innerHeight * 0.35;
    var layer = document.createElement('div');
    layer.className = 'dp-confetti';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);
    var pieces = [];
    for (var i = 0; i < n; i++) {
      var p = document.createElement('i');
      p.style.background = CONFETTI[i % CONFETTI.length];
      p.style.left = ox + 'px'; p.style.top = oy + 'px';
      if (i % 3 === 0) p.style.borderRadius = '50%';
      layer.appendChild(p);
      var ang = Math.random() * Math.PI * 2, sp = 6 + Math.random() * 9;
      pieces.push({ el: p, x: 0, y: 0, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp - 7, r: Math.random() * 360, vr: (Math.random() - 0.5) * 22 });
    }
    var t0 = performance.now();
    (function frame(t) {
      var k = (t - t0) / 1600;
      pieces.forEach(function (q) {
        q.vy += 0.38; q.vx *= 0.985; q.x += q.vx; q.y += q.vy; q.r += q.vr;
        q.el.style.transform = 'translate(' + q.x + 'px,' + q.y + 'px) rotate(' + q.r + 'deg)';
        q.el.style.opacity = String(Math.max(0, 1 - k * 0.9));
      });
      if (k < 1) requestAnimationFrame(frame); else layer.remove();
    })(t0);
  };

  /* ---------------- fly to cart ---------------- */
  function cartTarget() {
    var cands = document.querySelectorAll('.evd-hpn__icon--dark, a[href="/cart"], a[href$="/cart"], .evd-sh__cartbtn');
    for (var i = 0; i < cands.length; i++) {
      var r = cands[i].getBoundingClientRect();
      if (r.width && r.height && r.top < window.innerHeight && r.bottom > 0) return cands[i];
    }
    return null;
  }
  DP.fly = function (fromEl, imgSrc) {
    var target = cartTarget();
    if (!target || reduce.matches || !imgSrc) return;
    var a = fromEl.getBoundingClientRect(), b = target.getBoundingClientRect();
    var img = new Image();
    img.src = imgSrc; img.className = 'dp-fly'; img.alt = '';
    img.style.left = a.left + a.width / 2 - 36 + 'px';
    img.style.top = a.top + a.height / 2 - 36 + 'px';
    document.body.appendChild(img);
    var dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
    var anim = img.animate([
      { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
      { transform: 'translate(' + dx * 0.5 + 'px,' + (dy * 0.5 - 120) + 'px) scale(0.85) rotate(-20deg)', opacity: 1, offset: 0.5 },
      { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.25) rotate(-40deg)', opacity: 0.6 }
    ], { duration: 720, easing: 'cubic-bezier(0.5, 0, 0.3, 1)' });
    anim.onfinish = function () {
      img.remove();
      target.classList.remove('dp-bounce'); void target.offsetWidth; target.classList.add('dp-bounce');
    };
  };

  function onAtcClick(e) {
    var btn = e.target.closest('[data-evd-atc][data-variant-id]');
    if (!btn || btn.disabled) return;
    var holder = btn.closest('[data-dp-fly]');
    var img = holder && holder.querySelector('img');
    var src = btn.getAttribute('data-dp-img') || (img && (img.currentSrc || img.src));
    DP.fly(btn, src);
    if (!DP.store.get('first-add', false)) {
      DP.store.set('first-add', true);
      var r = btn.getBoundingClientRect();
      setTimeout(function () { DP.confetti({ x: r.left + r.width / 2, y: r.top, count: 60 }); }, 250);
    }
  }
  document.addEventListener('click', onAtcClick, true);

  /* Multi-item add that reuses the theme drawer (window.EVDCart) */
  DP.addItems = function (items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (d) { if (!r.ok) throw new Error(d.description || 'Could not add'); return d; });
    }).then(function (d) {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'add_to_cart', ecommerce: { items: (d.items || []).map(function (i) { return { item_id: String(i.variant_id), item_name: i.title, price: i.price / 100, quantity: i.quantity }; }) } });
      } catch (e) {}
      if (window.EVDCart) return window.EVDCart.refresh().then(function () { window.EVDCart.open(); return d; });
      window.location.href = '/cart';
      return d;
    });
  };

  /* ---------------- page transitions ---------------- */
  var supportsCrossDocVT = 'onpagereveal' in window && CSS.supports && CSS.supports('view-transition-name: a');

  /* Product image morph: mark the clicked card image so Horizon's pageswap handler
     types the transition as product-image-transition (PDP gallery carries the same type). */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href*="/products/"]');
    if (!a || e.defaultPrevented) return;
    var card = a.closest('[data-dp-morph]');
    var img = card && card.querySelector('[data-dp-morph-img]');
    if (!img) return;
    document.querySelectorAll('[data-view-transition-triggered]').forEach(function (n) { n.removeAttribute('data-view-transition-triggered'); });
    img.setAttribute('data-view-transition-type', 'product-image-transition');
    img.setAttribute('data-view-transition-triggered', '');
  });

  function isPlainNav(e, a) {
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download') || a.getAttribute('rel') === 'external') return false;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return false;
    var url;
    try { url = new URL(a.href, location.href); } catch (err) { return false; }
    if (url.origin !== location.origin) return false;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return false;
    if (/\/(cart|checkout|account\/logout)/.test(url.pathname) && a.closest('#evdCartDrawer')) return false;
    return url;
  }

  if (!supportsCrossDocVT && !reduce.matches && !designMode) {
    html.classList.add('dp-vt-fallback');
    var arriving = false;
    try { arriving = sessionStorage.getItem('dp-curtain') === '1'; sessionStorage.removeItem('dp-curtain'); } catch (e) {}
    if (arriving) {
      html.classList.add('dp-arriving');
      requestAnimationFrame(function () { requestAnimationFrame(function () {
        html.classList.remove('dp-arriving'); html.classList.add('dp-arrived');
      }); });
    }
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href]');
      var url = isPlainNav(e, a);
      if (!url) return;
      if (a.closest('#evdCartDrawer') || a.matches('a[href="/cart"], a[href$="/cart"]')) return; /* drawer handles these */
      e.preventDefault();
      try { sessionStorage.setItem('dp-curtain', '1'); } catch (err) {}
      html.classList.remove('dp-arrived');
      html.classList.add('dp-leaving');
      setTimeout(function () { window.location.href = url.href; }, 340);
    });
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) html.classList.remove('dp-leaving'); });
  }

  /* ---------------- chat launcher (Tidio app embed, open shadow root) ----------------
     The app stays on. Inside its shadow root: the greeting label is hidden on phones and folds
     away after a few seconds on desktop, and the closed bubble lifts above the PDP sticky bar. */
  (function chatLauncher() {
    var tries = 0;
    var css = '@media (max-width: 899px) { .widgetLabel { display: none !important; } }' +
      ':host(.dp-chat-quiet) .widgetLabel { display: none !important; }' +
      ':host(.dp-chat-lift) #button.chat-closed { transform: translateY(-84px); transition: transform .3s ease; }' +
      '#button.chat-closed { transition: transform .3s ease; }';
    function sync(host) {
      host.classList.toggle('dp-chat-lift', html.classList.contains('dp-has-sticky'));
    }
    function attach() {
      var host = document.getElementById('tidio-chat');
      var root = host && host.shadowRoot;
      if (!root) { if (++tries < 40) setTimeout(attach, 500); return; }
      if (!root.querySelector('style[data-dp]')) {
        var st = document.createElement('style'); st.setAttribute('data-dp', ''); st.textContent = css; root.appendChild(st);
      }
      setTimeout(function () { host.classList.add('dp-chat-quiet'); }, 4000);
      sync(host);
      new MutationObserver(function () { sync(host); }).observe(html, { attributes: true, attributeFilter: ['class'] });
    }
    if (document.readyState === 'complete') attach(); else window.addEventListener('load', attach);
  })();

  /* ---------------- boot ---------------- */
  function boot(root) {
    observe(root);
    bindTilt(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
